import {
  apiCallDial,
  apiCallCancel,
  apiCallEnable,
  apiGetCallPrices,
  apiCallHang,
  apiCallMatch,
  apiCallCancelMatch,
  apiMatchPoolDetail,
  apiNoRechargeFreeMatch,
  apiFreeMatchEnd,
  apiCallEndInfo
} from '@/api/call'
import { apiGetIsRecharged } from '@/api/recharge'
import type {
  callPricesInterface,
  matchPoolDetailInterface
} from '@/types/apiType/callType'
import type { userInfoStorageMapInterface } from '@/types/viewsType/userType'

export const useCallStore = defineStore({
  id: 'call',
  state: () => ({
    roomUid: 0 as number, // 房间Uid
    callUid: 0 as number, // 视频通话时对方或目标ID，一般在拨打中或匹配中使用，此字段不会重置 &，如果是拨打 callUid就是主播的uid，如果是接听 callUid就是拨打者的uid
    callId: 0 as number, // 通话id，route call type:10返回
    callPricesInfo: {} as callPricesInterface, // 呼叫者价格
    isStartBilling: false as boolean, // 是否开始计费
    isMatch: false, // 是否在匹配中，一般在等待匹配时使用
    isDial: false, // 是否在拨打中
    callTime: 0 as number, //通话时长 秒
    callType: 1 as 1 | 2, // 当前通话类型，1拨打 2匹配，一般在视频通话时使用
    isShowCallRouteLeavDialog: true as boolean, // 是否显示离开页面确认弹窗
    matchPoolDetail: {} as matchPoolDetailInterface, // 获取匹配信息 钻石，匹配免费券等
    isShowResumeCallPopup: false as boolean, // 复call弹窗 显示/关闭
    resumeCallUid: 0 as number, // 因在通话中充值导致断开通话，所以存此消息用于复call
    validCallAfterUids: {} as { [prop: string]: number }, // {uid:时间戳} 有效通话过的用户uid；给 已通话过的主播主动发信息给用户 触发埋点使用;
    callShortVideoDetailId: 0 as number, // 挂断接口用到,短视频通话状态记录ID
    callShortVideoUrl: '' as string, // 短视频电话视频链接
    callShortVideoRoomId: 0 as number, // 短视频电话视频roomId
    callShortVideoCallId: 0 as number, // 短视频通话状态记录ID
    /** 是否显示主动拨打通话等待中组件 */
    isShowCallWaitingPopup: false,
    /** 是否显示接听电话显示 */
    isShowCallAcceptPopup: false,
    /** socket收到视频电话，的用户信息 */
    callAcceptInfo: {
      callUid: 0 as number,
      nick: '' as string,
      avatar: '' as string
    },
    /** 是否执行挂断api callType8触发的就不执行 */
    isHangApi: true as boolean,
    /** 匹配 -重新匹配 弹窗配置,目前给匹配免费券使用 */
    matchBackPopupConfig: {
      isShow: false,
      isLazyShow: false,
      text: useNuxtApp().$i18n.t('match.matchBackTips') as string,
      /** 匹配到的主播uid */
      matchUid: 0 as number
    }
  }),
  getters: {
    /** callUid的用户信息 */
    callUidUserInfoStorage: (state): userInfoStorageMapInterface => {
      const userStore = useUserStore()
      return userStore.userInfoStorageMap[state.callUid] || {}
    }
  },
  actions: {
    /***
     * 设置匹配 -重新匹配 弹窗配置,目前给匹配免费券使用
     */
    setMatchBackPopupConfig(value: boolean) {
      this.matchBackPopupConfig.isShow = value
      this.matchBackPopupConfig.isLazyShow = true
    },
    /** 设置callId */
    setCallId(id: number) {
      this.callId = id
    },
    /** App视频通话设置初始化数据
     * @param {number} roomUid
     * @param {number} callUid
     */
    initAppCall(roomUid: number, callUid: number) {
      this.callUid = callUid
      this.roomUid = roomUid
      this.isDial = true
      this.callType = 1
    },
    /**
     * 设置是否显示接听电话显示
     */
    setIsShowCallAcceptPopup(value: boolean) {
      this.isShowCallAcceptPopup = value
    },
    /**
     * 设置是否显示通话等待中组件
     */
    setIsShowCallWaitingPopup(value: boolean) {
      this.isShowCallWaitingPopup = value
    },
    /**
     * 设置是否显示离开页面确认弹窗
     * @param {boolean} value 开关
     */
    setIsShowCallRouteLeavDialog(value: boolean) {
      this.isShowCallRouteLeavDialog = value
    },
    // 设置有效通话过的用户uid
    setValidCallAfterUids(lastMsg) {
      // 如果是扩展字段 自定义字段
      if (lastMsg.type === 'custom') {
        const customContent = JSON.parse(lastMsg.content)
        // 如果是有效的通话时间
        if (customContent.first === 162 && customContent.second === 1) {
          // 存有效通话过的用户uid
          this.validCallAfterUids[lastMsg.to] = lastMsg.time
        }
      }
    },
    // 给 已通话过的主播主动发信息给用户 触发埋点使用;
    validCallAfterMessageEventLog(uid: number, time: number) {
      // 判断是否有此uid，再判断信息时间戳是否大于 validCallAfterUids 已存的时间戳
      if (
        this.validCallAfterUids[uid] &&
        time >= this.validCallAfterUids[uid]
      ) {
        delete this.validCallAfterUids[uid]
        eventTracking.eventLog(eventTracking.eventId.call_after_message) // 有效电话通话后，主播给用户回复信息
      }
    },
    // 设置复播消息
    setResumeCallInfo(isShow: boolean, id: number) {
      this.isShowResumeCallPopup = isShow
      this.resumeCallUid = id
    },
    // 开始计费 直播SDK使用
    onStartBilling() {
      const i18n = useNuxtApp().$i18n
      // 确保只执行一次
      if (!this.isStartBilling) {
        this.isStartBilling = true
        const roomStore = useRoomStore()
        roomStore.setIsStartTimer(true)
        AppNative.appFun(AppNative.iosNeedMessageFun.startBilling)
        // 开始计费，推送开始视频公屏
        roomStore.updateChatList('put', {
          content: i18n.t('room.startVideoChatTips'),
          type: constant.ROOM_CHAT_TYPE.PROMPT_TEXT
        })
      }
    },
    /**
     * 收到电话呼叫
     */
    onCallAccept(custom) {
      this.callUid = custom.callUid
      this.callAcceptInfo.callUid = custom.callUid
      this.callAcceptInfo.nick = custom.nick
      this.callAcceptInfo.avatar = custom.avatar
      this.setIsShowCallAcceptPopup(true)
    },
    // 通话挂断
    onCallHang(msg: string) {
      const i18n = useNuxtApp().$i18n
      const router = useRouter()
      const route = useRoute()
      const publicStore = usePublicStore()
      const roomStore = useRoomStore()
      showToast(msg === 'call end' || !msg ? i18n.t('call.callEnd') : msg)
      publicStore.setRechargeTipsPopup(false, '') // 重置关闭充值弹窗
      if (route.path.indexOf('/room/call') !== -1 && this.roomUid !== 0) {
        // 如果是socket回调的关闭，不显示弹窗
        this.setIsShowCallRouteLeavDialog(false)
        this.isHangApi = false
        if (!roomStore.isApp) {
          router.back()
        }
      }
    },
    // 通话关闭
    onCallClose(msg: string) {
      const i18n = useNuxtApp().$i18n
      const router = useRouter()
      const route = useRoute()
      const publicStore = usePublicStore()
      const roomStore = useRoomStore()
      showToast(msg === 'call end' || !msg ? i18n.t('call.callEnd') : msg)
      publicStore.setRechargeTipsPopup(false, '') // 重置关闭充值弹窗
      if (route.path.indexOf('/room/call') !== -1 && this.roomUid !== 0) {
        // 如果是socket回调的关闭，不显示弹窗
        this.setIsShowCallRouteLeavDialog(false)
        this.isHangApi = true
        if (!roomStore.isApp) {
          router.back()
        }
      }
    },
    // 拨号接听成功 roomUid,type 1:拨打，2：匹配，videoType电话类型：1：视频电话，短视频电话
    async onCallSuccess(roomUid, type: 1 | 2, videoType: 1 | 2) {
      const router = useRouter()
      const agoraStore = useAgoraStore()
      if (agoraStore.joined) {
        await agoraStore.leave()
      }

      const trtcStore = useTrtcStore()
      if (trtcStore.joined) {
        await trtcStore.exit()
      }

      const roomStore = useRoomStore()
      this.isHangApi = true
      this.callType = type
      this.roomUid = roomUid
      if (this.callType === 2) {
        this.callUid = roomUid
        this.getCallPrices(2)
      }
      roomStore.getAnchorInfo(roomUid)
      const time = this.callType === 1 ? 2000 : 3500

      setTimeout(() => {
        this.setIsShowCallWaitingPopup(false)
        // 进入通话页面同时重置 isShowCallRouteLeavDialog
        this.setIsShowCallRouteLeavDialog(true)
        // 如果当前在房间内就手动退出一次房间
        roomStore.roomId && roomStore.quit()
        // 如果是拨打通话，把拨打状态设置成true
        if (this.callType === 1) {
          this.isDial = true
        }
        router.replace({
          path: videoType === 1 ? '/room/call' : '/room/callShortVideo'
        })
      }, time)
    },
    /** 拨号被拒绝
     */
    onCallRejected(custom) {
      const i18n = useNuxtApp().$i18n
      // 接收到电话，被取消
      if (custom.refuseType === -1) {
        console.log('接收到电话，被取消')
        this.setIsShowCallAcceptPopup(false)
      }
      // 等待超时对方没接听
      if (custom.refuseType === 0) {
        this.setIsShowCallWaitingPopup(false)
      }
      // 主动拨打电话被被拒绝接听
      if (custom.refuseType === 1) {
        showToast(i18n.t('call.callRejectedTips'))
        // 发送拒绝云信消息
        const chatStore = useChatStore()
        const userStore = useUserStore()
        const content = {
          first: 160,
          second: 1,
          data: {
            avatar: userStore.userInfo.avatar,
            message: 'Reject',
            time: 0
          }
        }
        chatStore.sendMsg({
          type: 'call',
          scene: 'p2p',
          to: custom.refuseUid,
          content: content
        })
        this.setIsShowCallWaitingPopup(false)
      }
    },
    /**
     * 拨号
     * @param {number} targetUid 目标ID
     * @param {0|1} match 0正常拨号，1匹配拨号，目前H5没有1匹配拨号
     * @param {1|4|5|6|8} source  网页端：1首页，4直播间，5私聊，6个人弹窗, 101充值回调复拨
     */
    dial(targetUid: number, match: 0 | 1, source: 1 | 4 | 5 | 6 | 101) {
      const route = useRoute()
      const i18n = useNuxtApp().$i18n
      // 重置回调复拨uid
      this.matchBackPopupConfig.matchUid = 0
      // 判断是否在拨打中
      if (this.isDial || this.isShowCallWaitingPopup) {
        showToast(i18n.t('call.duringCallTips'))
        return
      }

      // 先判断是否在授权中
      showLoadingToast({
        message: '',
        forbidClick: true,
        duration: 0
      })
      const agoraStore = useAgoraStore()
      agoraStore
        .checkTracks()
        .then(async () => {
          // 授权成功
          const res = await apiCallDial({
            targetUid,
            match,
            callType: source,
            useFree: false
          })
          const { code } = res
          if (code === 200) {
            // 预加载1v1页面
            preloadRouteComponents('/room/call')

            this.callUid = targetUid
            this.getCallPrices(1)

            closeToast()

            // 如果是直播间来源拨打，就暂时关闭router跳转拦截 || 在房间拨打的都直接 关闭router跳转拦截
            if (source === 4 || route.path.indexOf('/room') > -1) {
              const roomStore = useRoomStore()
              roomStore.setIsShowLiveRouteLeavDialog(false)
            }

            // 埋点 跳转前埋点
            switch (source) {
              case 1:
                eventTracking.eventLog(
                  eventTracking.eventId.home_popular_video_call_click
                ) // 首页拨打
                break
              case 4:
                eventTracking.eventLog(
                  eventTracking.eventId.click_room_video_call
                ) // 直播间拨打
                break
              case 5:
                eventTracking.eventLog(
                  eventTracking.eventId.chat_video_call_click
                ) // 私聊拨打
                break
              case 6:
                eventTracking.eventLog(
                  eventTracking.eventId.click_user_info_video_call
                ) // 个人弹窗拨打
                break
              case 101:
                eventTracking.eventLog(
                  eventTracking.eventId.click_recharge_resume_call
                ) // 充值回调复call
                break
            }

            // 有几率接口未响应返回就先收到socket信息, 如果在通话界面就不走跳转逻辑,要用路由判断
            if (route.path.indexOf('/room/call') === -1) {
              this.setIsShowCallWaitingPopup(true)
            }
          }
          // 显示余额不足弹窗
          if (code === 2104) {
            const publicStore = usePublicStore()
            // 如果是直播间或私聊，直接显示充值弹窗
            if (source === 4 || source === 5) {
              publicStore.setShowRechargePopup(true)
            } else {
              // 其他其实确认框
              publicStore.setRechargeTipsPopup(true, 1)
            }
          } else {
            if (res.message && res.message !== 'success') {
              res.message && showToast(res.message)
            }
          }
        })
        .catch((error) => {
          console.log('dial授权', error)
          closeToast()
        })
    },
    /**
     * 匹配
     * @param {1|4} source  网页端：1匹配页，4房间
     */
    match(source: 1 | 4) {
      const i18n = useNuxtApp().$i18n
      const router = useRouter()
      // 重置回调复拨uid
      this.matchBackPopupConfig.matchUid = 0
      // 先判断是否在授权中
      showLoadingToast({
        message: '',
        forbidClick: true,
        duration: 0
      })
      const agoraStore = useAgoraStore()
      agoraStore
        .checkTracks()
        .then(async () => {
          // 授权成功
          const detailRes = await apiMatchPoolDetail()
          const { code: detailCode, data: poolDetail } = detailRes
          if (detailCode === 200) {
            this.matchPoolDetail = poolDetail
            // 判断优惠券数量，判断钻石是否足够
            const userStore = useUserStore()
            if (
              poolDetail.freeCount <= 0 &&
              poolDetail.diamond > userStore.userInfo.diamondNum
            ) {
              closeToast()
              // 显示余额不足弹窗
              const publicStore = usePublicStore()
              // 如果是直播间拨打，直接显示充值弹窗
              if (source === 4) {
                publicStore.setShowRechargePopup(true)
              } else {
                // 其他其实确认框
                publicStore.setRechargeTipsPopup(
                  true,
                  i18n.t('call.matchNotDiamondTips', [poolDetail.diamond])
                )
              }

              return
            }
            // 判断是否有免费券和是否没充值过，是就进行短视频电话
            if (poolDetail.freeCount > 0) {
              const isRechargedRes = await apiGetIsRecharged()
              if (isRechargedRes.code === 200) {
                // 没充值过
                if (!isRechargedRes.data) {
                  const freeMatchRes = await apiNoRechargeFreeMatch()
                  const { code: freeMatchCode, data: freeMatchData } =
                    freeMatchRes
                  if (freeMatchCode === 200) {
                    const userStore = useUserStore()
                    this.isMatch = true
                    closeToast()
                    if (source === 4) {
                      // 直播间来源要先关闭路由跳转拦截弹窗
                      this.setIsShowCallRouteLeavDialog(false)
                    }
                    setTimeout(() => {
                      // 设置短视频电话需要的参数，延迟一秒是为了再次匹配时数据格式化
                      userStore.publicSetUserInfo(freeMatchData.users)
                      this.callShortVideoDetailId = freeMatchData.callDetailId
                      this.callShortVideoUrl = freeMatchData.videoUrl
                      this.callShortVideoRoomId = freeMatchData.roomId
                      this.callShortVideoCallId = freeMatchData.callId
                      this.callUid = freeMatchData.users.uid
                      this.matchBackPopupConfig.matchUid =
                        freeMatchData.users.uid
                      // 跳转短视频房
                      this.onCallSuccess(freeMatchData.users.uid, 2, 2)
                    }, 1000)
                    // 如果在是视频通话页面匹配的，就replace跳转
                    // 埋点  跳转前埋点
                    switch (source) {
                      case 1:
                        eventTracking.eventLog(
                          eventTracking.eventId.video_start_match
                        )
                        if (userStore.isNewUser) {
                          eventTracking.eventLog(
                            eventTracking.eventId.video_start_match_new_user
                          )
                        }
                        break
                      case 4:
                        // 匹配下一个埋点
                        eventTracking.eventLog(
                          eventTracking.eventId.exit_by_match_another
                        )
                        break
                    }
                    if (
                      router.currentRoute.value.path.indexOf('/room/call') !==
                      -1
                    ) {
                      router.replace('/match/matching')
                    } else {
                      router.push('/match/matching')
                    }

                    // return 去短视频通话界面
                    return
                  } else if (freeMatchCode === 8016) {
                    // 走正常匹配逻辑
                  } else {
                    showToast(i18n.t('public.frequentOperationTips'))
                    return
                  }
                }
              } else {
                closeToast()
                showToast(i18n.t('public.frequentOperationTips'))
                return
              }
            }

            const res = await apiCallMatch()
            const { code } = res
            if (code === 200) {
              // 预加载1v1页面
              preloadRouteComponents('/room/call')

              this.isMatch = true
              closeToast()

              // 埋点  跳转前埋点
              switch (source) {
                case 1:
                  eventTracking.eventLog(
                    eventTracking.eventId.video_start_match
                  )
                  if (userStore.isNewUser) {
                    eventTracking.eventLog(
                      eventTracking.eventId.video_start_match_new_user
                    )
                  }
                  break
                case 4:
                  // 匹配下一个埋点
                  eventTracking.eventLog(
                    eventTracking.eventId.exit_by_match_another
                  )
                  break
              }

              if (source === 4) {
                // 直播间来源要先关闭路由跳转拦截弹窗
                this.setIsShowCallRouteLeavDialog(false)
              }
              // 如果在是视频通话页面匹配的，就replace跳转
              if (router.currentRoute.value.path.indexOf('/room/call') !== -1) {
                router.replace('/match/matching')
              } else {
                router.push('/match/matching')
              }
            }
          }
        })
        .catch((error) => {
          console.log('match授权', error)
          closeToast()
        })
    },
    /**
     * 取消匹配
     */
    async cancelMatch() {
      this.isMatch = false
      await apiCallCancelMatch()
    },
    /**
     * 获取匹配信息
     * @param {boolean} isShowMatchBackPopup 是否显示匹配返回弹窗
     */
    async getMatchPoolDetail(param?: { isShowMatchBackPopup?: boolean }) {
      const detailRes = await apiMatchPoolDetail()
      const { code, data } = detailRes
      if (code === 200) {
        this.matchPoolDetail = data
        // 目前给退出房间时使用，有免费券数量就显示
        if (
          param?.isShowMatchBackPopup &&
          this.matchPoolDetail.freeCount >= 0
        ) {
          this.setMatchBackPopupConfig(true)
        }
      }
    },
    /**
     * 接听-允许 暂时不做
     * @param {number} targetUid 目标ID
     */
    async enable(targetUid: number) {
      return await apiCallEnable({ targetUid })
    },
    /**
     * 拒绝接听或取消拨号
     * @param {0|1|2} refuseType 2匹配拒绝 1拒绝接听 0超时
     */
    async cancel(refuseType: 0 | 1 | 2) {
      this.isDial = false
      this.setIsShowCallWaitingPopup(false)
      await apiCallCancel({ calledUid: this.callUid, refuseType })
    },
    /**
     * 获取被呼叫人的通话价格
     * @param {number} priceUid
     * @param {1|2} type // 1：拨打，2：匹配
     */
    async getCallPrices(type) {
      const res = await apiGetCallPrices({ priceUid: this.callUid, type })
      const { code, data } = res
      if (code === 200) {
        this.callPricesInfo = data
      }
    },
    // 退出
    quit() {
      console.log('通话退出接口,', this.roomUid)
      if (this.roomUid) {
        // 调用结束通话接口
        const roomStore = useRoomStore()
        const userStore = useUserStore()
        // web使用
        if (
          roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL &&
          !roomStore.isApp
        ) {
          if (this.isHangApi) {
            // 视频电话关闭通话
            apiCallHang({ roomId: roomStore.roomId })
          }
          // 通话结束信息接口
          apiCallEndInfo({
            callId: this.callId,
            roomId: roomStore.roomId
          }).then((res) => {
            const { code, data } = res
            // flowTip 为true就弹窗，是因为拨打了10次电话积累
            if (code === 200 && data.flowTip) {
              const publicStore = usePublicStore()
              publicStore.setDownloadAppTipsConfig(true, 1, 'call')
            }
          })

          // 发送结束云信消息
          const chatStore = useChatStore()
          const content = {
            first: 162,
            second: 1,
            data: {
              avatar: userStore.userInfo.avatar,
              message: 'Video',
              time: this.callTime * 1000 //传的毫秒
            }
          }
          chatStore.sendMsg({
            type: 'call',
            scene: 'p2p',
            to: this.callUid,
            content
          })
        } else if (roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL_SHORT) {
          // 短视频电话就跳接口断一下
          apiFreeMatchEnd({
            callId: this.callShortVideoCallId,
            callDetailId: this.callShortVideoDetailId,
            second: this.callTime
          })
        }

        userStore.requestUserInfo()

        this.getMatchPoolDetail({
          isShowMatchBackPopup:
            roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL_SHORT &&
            !!this.matchBackPopupConfig.matchUid
        })

        this.resetData()
      }
    },
    // 重置数据
    resetData() {
      this.isStartBilling = false
      this.roomUid = 0
      this.callId = 0
      this.callShortVideoDetailId = 0
      this.callShortVideoRoomId = 0
      this.callShortVideoCallId = 0
      this.matchPoolDetail = { freeCount: 0, diamond: 0, callPrice: 0 }
      this.validCallAfterUids = {}
      this.callShortVideoUrl = ''
      this.isShowCallWaitingPopup = false
      this.isDial = false
      this.isHangApi = true
    }
  },
  persist: {
    key: 'callStore',
    paths: ['validCallAfterUids']
  }
})
